import { isPlatformServer } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewChild,
  inject,
} from '@angular/core';
import { Subject, interval, takeUntil } from 'rxjs';
import { getSafeAreaInsets } from '../../../lib/get-min-safe-area';
import { throttle } from '../../../lib/throttle';

@Component({
  selector: 'app-sticky',
  templateUrl: './sticky.component.html',
  styleUrls: ['./sticky.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StickyComponent implements OnInit, OnDestroy {
  private platformId = inject(PLATFORM_ID);
  private check = inject(ChangeDetectorRef);
  private zone = inject(NgZone);
  @ViewChild('element') element: ElementRef;
  @Input() container: HTMLElement;
  @Input() paddingX = 0;
  @Input() paddingY = 0;
  @Input() paddingYBottom = 0;
  @Input() getPaddingY: () => number;
  @Input() offsetTop = 0;
  @Input() offsetBottom = 0;
  @Input() minHeight = 0;
  @Input() zIndex = 49;
  @Input() extraYSpacing = 0;
  @Input() autoFill: HTMLElement;
  @Input() fullWidth: boolean;
  @Input() stickTo: 'top' | 'bottom' = 'top';
  @Input() left: boolean;

  @Output() fixedChange = new EventEmitter<boolean>();
  fixed: boolean;
  atBottom: boolean;
  atTop: boolean;
  height: number;
  heightObserver: ResizeObserver;
  destroyed$ = new Subject<void>();
  safeArea = getSafeAreaInsets();

  @HostListener('window:scroll')
  onScroll() {
    this.calcThrottled();
  }

  async ngOnInit() {
    if (isPlatformServer(this.platformId)) return;
    this.calcThrottled();
    this.heightObserver = new ResizeObserver(this.calcThrottled.bind(this));
    if (this.container) {
      this.heightObserver.observe(this.container);
    }
    this.zone.runOutsideAngular(() => {
      interval(500)
        .pipe(takeUntil(this.destroyed$))
        .subscribe(() => this.calcThrottled());
    });
  }

  calcThrottled = throttle(() => {
    this.calc();
  }, 16);

  calc() {
    if (isPlatformServer(this.platformId)) return;
    const bounds = this.container?.getBoundingClientRect();
    if (!bounds) return;

    if (this.getPaddingY) {
      this.paddingY = this.getPaddingY();
    }

    const top = -bounds.top + this.offsetTop + this.paddingY;
    const elHeight = this.element ? this.element.nativeElement.getBoundingClientRect().height : 0;
    const containerHeight = bounds.height - elHeight;

    if (this.stickTo === 'top') {
      this.atBottom = top > containerHeight - (this.paddingYBottom ?? this.paddingY);
      const _top = -bounds.top + this.offsetTop;
      if (_top < 0) this.fixed = false;
      else this.fixed = !this.atBottom;
    } else {
      const bottom =
        window.innerHeight - bounds.y - elHeight - this.paddingY - Math.max(this.offsetBottom, this.safeArea.bottom);
      this.atTop = bottom < this.paddingY;

      const _bottom = window.innerHeight - bounds.bottom - Math.max(this.offsetBottom, this.safeArea.bottom);

      if (_bottom > 0) {
        this.fixed = false;
      } else {
        this.fixed = !this.atTop;
      }
    }

    if (this.autoFill) {
      const autoFillBounds = this.autoFill.getBoundingClientRect();
      const bottom = window.innerHeight - autoFillBounds.bottom + this.extraYSpacing;
      const top = autoFillBounds.top - this.offsetTop + this.extraYSpacing;

      let height = autoFillBounds.height - this.paddingY * 2 - this.extraYSpacing * 2;
      if (bottom < 0) height += bottom;
      if (top < 0) height += top;

      if (height < this.minHeight) height = this.minHeight;

      this.height = height;
    }

    this.fixedChange.emit(this.fixed);

    this.check.detectChanges();
  }

  offsetBottomWithSafeArea() {
    return Math.max(this.offsetBottom, this.safeArea.bottom);
  }

  ngOnDestroy(): void {
    this.heightObserver?.disconnect();
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
